import React, { useCallback, useMemo, useState } from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'

import { CalendarToday } from '@mui/icons-material'
import { DateTimePicker } from '@mui/x-date-pickers'

import createLogger from '~/src/Lib/Logging'
import { datePropType, stylePropType } from '~/src/Lib/PropTypes'
import { formattedDate, getDateTime } from '~/src/Lib/Utils'
import Button from '~/src/UI/Shared/Button'
import TextField from '~/src/UI/Shared/Form/TextField'

import Null from '../Null'

const noop = () => {}
const dialogProps = { PaperProps: { style: { minWidth: 340 } } }
const logger = createLogger('DateTimePicker')
/**
 * A time picker field that can be rendered as a text field or a button.
 * Both open a time picker modal on click/touch.
 * @component
 * @alias DateTimePicker
 * @named_export DateTimePickerComponent
 * @composes
 * from: '@mui/x-date-pickers/DateTimePicker'
 * link: https://material-ui-pickers.dev/api/DateTimePicker
 * @example
 * import { formattedDate } from '~/src/Lib/Utils'
 * {() => {
 *   const [time, setTime] = React.useState('2012-01-05T17:00:00.000-0700')
 *   const customDateTimeFormat = {
 *     dayPeriod: 'short',
 *     month: 'numeric',
 *     day: 'numeric',
 *     hour: 'numeric',
 *     minute: 'numeric',
 *     timeZoneName: 'short',
 *   }
 *   return (
 *     <Box display="flex" alignItems="center" style={{ gap: '1rem' }}>
 *       <T>Date: {formattedDate(time, 'FULL_TIME')}</T>
 *       <DateTimePicker value={time} onChange={setTime} />
 *       <DateTimePicker button value={time} onChange={setTime} />
 *       <DateTimePicker button buttonDateFormat={customDateTimeFormat} value={time} onChange={setTime} />
 *     </Box>
 *   )
 * }}
 */
export const DateTimePickerComponent = ({
  button = false,
  buttonDateFormat = 'SHORT_DATETIME',
  className = '',
  clearable = true,
  error: propsError = undefined,
  fullWidth = false,
  helperText: propsHelperText = undefined,
  margin = 'none',
  name = null,
  value = '',
  onChange: propsOnChange,
  ...props
}) => {
  // eslint-disable-next-line react/no-unstable-nested-components
  const TextFieldComponent = useMemo(() => ({
    clearable: ignore,
    error,
    helperText,
    onChange: tfOnChange = noop,
    ...tfProps
  }) => (
    <TextField
      {...tfProps}
      margin={margin}
      error={(error && helperText) || propsError}
      helperText={helperText || propsHelperText}
      fullWidth={fullWidth}
      onChange={tfOnChange}
    />
  ), [fullWidth, margin, propsError, propsHelperText])
  const onChange = useCallback(
    date => propsOnChange(name ? { [name]: date } : date),
    [name, propsOnChange]
  )
  const [isOpen, setIsOpen] = useState(false)
  let dateValue = value ? getDateTime(value) : null
  if (dateValue && !dateValue.isValid) dateValue = null

  const buttonProps = button ? ({
    open: isOpen,
    onOpen: () => setIsOpen(true),
    onClose: () => setIsOpen(false),
    TextFieldComponent: Null
  }) : {}

  return (
    <>
      <DateTimePicker
        data-testid="date-picker"
        className={classNames(className, 'aroya__datetime-picker')}
        clearable={Boolean(clearable && value)}
        DialogProps={dialogProps}
        format="f"
        orientation="portrait"
        placeholder="Press enter to activate calendar"
        {...buttonProps}
        {...props}
        name={name}
        slots={{ textField: TextFieldComponent }}
        sx={{
          '&.aroya__datetime-picker:not(:has(label)) input': {
            paddingTop: '1rem',
            paddingBottom: '1rem',
          }
        }}
        value={dateValue}
        onAccept={onChange}
        onChange={noop}
      />
      {button && (
        <Button
          color="primary"
          endIcon={<CalendarToday />}
          onClick={() => {
            setIsOpen(true)
          }}
          variant="text"
        >
          {formattedDate(dateValue, buttonDateFormat)}
        </Button>
      )}
    </>
  )
}
DateTimePickerComponent.displayName = 'DateTimePicker'
DateTimePickerComponent.propTypes = {
  clearable: PropTypes.bool,
  margin: PropTypes.string,
  fullWidth: PropTypes.bool,
  error: PropTypes.node,
  helperText: PropTypes.node,
  label: PropTypes.string.isRequired,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, datePropType]),
  onChange: PropTypes.func.isRequired,
  style: stylePropType,
  button: PropTypes.bool,
  buttonDateFormat: PropTypes.string,
}

export const FormikDateTimePicker = React.memo(props => {
  const { field, form, alwaysShowError, ...passthru } = props
  const { setFieldTouched, setFieldValue } = form
  const onChange = useCallback(
    ({ [field.name]: value }) => {
      setFieldValue(field.name, value, true)
      if (value) {
        setFieldTouched(field.name, true, false)
      }
    },
    [field.name, setFieldTouched, setFieldValue]
  )

  const { error, touched } = form.getFieldMeta(field.name)

  return (
    <DateTimePickerComponent
      {...passthru}
      {...field}
      error={form.dirty && (touched || alwaysShowError) && error}
      onChange={onChange}
    />
  )
})

export default React.memo(DateTimePickerComponent)
